import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { PortfolioItem } from "@/hooks/profile";
import { toast } from 'sonner';
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { Button } from "@/components/ui/button";
import { useIsMobile } from '@/hooks/use-mobile';
import { usePortfolioAutosave } from '@/hooks/domains/portfolio/usePortfolioAutosave';
import { ShotDetailsCard } from "./shots/ShotDetailsCard";
import { ShotImagesCard } from "./shots/ShotImagesCard";

interface AddPortfolioItemFormProps {
  onAddItem: (item: PortfolioItem) => void;
  onCancel: () => void;
  editingItem?: PortfolioItem;
  userProfession?: string | null;
  userSkills?: string[] | null;
  initialImages?: string[];
  initialData?: Partial<PortfolioItem>; // For imported shot data with title/description
  onSaveStatusChange?: (status: 'idle' | 'saving' | 'saved' | 'error') => void;
  onItemChange?: (item: PortfolioItem) => void; // New prop for item updates
  hideButtons?: boolean; // New prop to hide Cancel/Publish buttons when used in editor page
}

const createInitialPortfolioItem = (
  initialImages?: string[],
  initialData?: Partial<PortfolioItem>
): PortfolioItem => ({
  id: initialData?.id || crypto.randomUUID(), // Generate UUID for new items
  title: initialData?.title || "",
  description: initialData?.description || "",
  image_url: initialImages && initialImages.length > 0 ? initialImages[0] : "",
  images: initialImages && initialImages.length > 0
    ? initialImages.map((url, idx) => ({
        id: crypto.randomUUID(),
        url,
        order: idx,
        caption: '',
        isLegacy: false
      }))
    : [],
  created_at: new Date().toISOString(),
  status: "draft",
});

const AddPortfolioItemForm = forwardRef(({
  onAddItem,
  onCancel,
  editingItem,
  userProfession,
  userSkills,
  initialImages,
  initialData,
  onSaveStatusChange,
  onItemChange,
  hideButtons = false,
}: AddPortfolioItemFormProps, ref) => {
  const [item, setItem] = useState<PortfolioItem>(
    editingItem || createInitialPortfolioItem(initialImages, initialData)
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isAutoGenerating, setIsAutoGenerating] = useState(false);
  const [detectedElements, setDetectedElements] = useState<string[]>([]);
  const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(false);
  const [isImagesCollapsed, setIsImagesCollapsed] = useState(false);

  const { user } = useAuth();
  const isMobile = useIsMobile();

  // Synchronize internal state when an editing item loads with fresh data (e.g., opening an existing draft)
  React.useEffect(() => {
    if (!editingItem) {
      return;
    }

    setItem((current) => {
      const hasChanged =
        editingItem.id !== current.id ||
        editingItem.title !== current.title ||
        editingItem.description !== current.description ||
        (editingItem.images?.length || 0) !== (current.images?.length || 0) ||
        editingItem.updated_at !== current.updated_at;

      if (hasChanged) {
        return editingItem;
      }

      return current;
    });
  }, [editingItem]);

  // Autosave hook for silent draft saves (only when not used in editor page)
  const { saveStatus, currentItemId, isSaving } = usePortfolioAutosave({
    portfolioItem: item,
    itemId: editingItem?.id || undefined, // Only pass ID if editing existing item
    debounceMs: 2000,
    enabled: !hideButtons && Boolean(item.title?.trim() || item.description?.trim() || item.images?.length), // Disable when used in editor page
    onSaveSuccess: (savedItem) => {
      console.log('[AddPortfolioItemForm] Autosave successful:', savedItem.id);
      // Don't overwrite user's current input - only update metadata fields
      // This prevents race condition where saved data overwrites newer user input
      setItem(prev => ({
        ...prev,
        id: savedItem.id,
        created_at: savedItem.created_at,
        updated_at: savedItem.updated_at,
        profile_id: savedItem.profile_id,
        // Keep user's current form data for all editable fields
      }));
    },
    onSaveError: (error) => {
      console.error('[AddPortfolioItemForm] Autosave failed:', error);
      toast.error('Failed to auto-save your changes. Please try again.');
    },
  });

  // Notify parent of save status changes
  React.useEffect(() => {
    onSaveStatusChange?.(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  const handleItemChange = (updatedItem: PortfolioItem) => {
    setItem(updatedItem);
    onItemChange?.(updatedItem); // Notify parent component of changes
  };

  // Get image URLs from the item
  const getImageUrls = (): string[] => {
    const urls: string[] = [];
    
    // Add legacy image_url if it exists
    if (item.image_url) {
      urls.push(item.image_url);
    }
    
    // Add images from the images array
    if (item.images && Array.isArray(item.images)) {
      item.images.forEach(img => {
        if (img.url) {
          urls.push(img.url);
        }
      });
    }
    
    return urls;
  };

  // Check if we have images uploaded
  const hasImages = () => {
    const imageUrls = getImageUrls();
    return imageUrls.length > 0;
  };

  const validateForm = (): string | null => {
    if (!user) {
      return "You must be logged in to add portfolio items";
    }

    if (!item.title.trim() || !item.description.trim()) {
      return "Please provide a title and description for your project";
    }

    if (!item.image_url && (!item.images || item.images.length === 0)) {
      return "Please add at least one image to your project";
    }

    return null;
  };

  const handleSubmit = (asDraft: boolean = false) => {
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    // Use the autosaved item ID if available, otherwise use existing or generate new
    const finalId = currentItemId || (editingItem ? item.id : crypto.randomUUID());

    const itemToSubmit = {
      ...item,
      id: finalId,
      created_at: item.created_at || new Date().toISOString(),
      status: asDraft ? "draft" as const : "published" as const,
    };

    onAddItem(itemToSubmit);
  };

  const isFormValid = Boolean(
    item.title.trim() &&
      item.description.trim() &&
      (item.image_url || (item.images && item.images.length > 0))
  );

  useImperativeHandle(ref, () => ({
    generateTitleAndDescription: () => {
      // This is no longer needed as generation is handled within ProjectDetailsFields
      console.log('generateTitleAndDescription called from ref');
    }
  }));

  return (
    <div className="bg-white rounded-2xl shadow p-6 md:p-8 w-full space-y-6">
      {/* Shot Details Card - Collapsible */}
      <ShotDetailsCard
        item={item}
        onChange={handleItemChange}
        isCollapsed={isDetailsCollapsed}
        onToggleCollapse={() => setIsDetailsCollapsed(!isDetailsCollapsed)}
        imageUrls={getImageUrls()}
        userProfession={userProfession || 'designer'}
        userSkills={userSkills || []}
        isAutoGenerating={isAutoGenerating}
        detectedElements={detectedElements}
      />

      {/* Shot Images Card - Collapsible */}
      <ShotImagesCard
        item={item}
        onChange={handleItemChange}
        isCollapsed={isImagesCollapsed}
        onToggleCollapse={() => setIsImagesCollapsed(!isImagesCollapsed)}
        onUploadStatusChange={setIsUploading}
      />

      {/* Conditionally render buttons based on hideButtons prop */}
      {!hideButtons && (
        <>
          {isMobile ? (
            <div className="flex flex-row w-full gap-2">
              <Button
                variant="outline"
                onClick={onCancel}
                className="w-1/2 h-12 text-base"
              >
                Cancel
              </Button>
              <Button
                className="w-1/2 bg-black text-white hover:bg-black/90 h-12 text-base justify-center"
                disabled={!isFormValid || isUploading || isAutoGenerating || isSaving}
                onClick={() => handleSubmit(false)}
              >
                {!editingItem ? "Publish" : editingItem.status === "draft" ? "Publish" : "Update"}
              </Button>
            </div>
          ) : (
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                onClick={onCancel}
                disabled={isUploading || isAutoGenerating}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleSubmit(false)}
                disabled={!isFormValid || isUploading || isAutoGenerating || isSaving}
              >
                {!editingItem ? "Publish" : editingItem.status === "draft" ? "Publish" : "Update"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default AddPortfolioItemForm;
